import API from '../Services/API';
import Model from './Model';

const useJournal = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'journal',
        stateKey: ['notifications', 'journal'],
        fetchData: async () => API.get('/notifications'),
        registerAdditionalMethods: (opts) => {
            return {
                updateNotificationPreferences: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/notifications/preferences', data),
                        null,
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading notifications.',
    })
}

const useJournalCounts = () => {
    return Model({
        alwaysRefresh: true,
        returnDataKey: 'counts',
        stateKey: ['notifications', 'journalCounts'],
        fetchData: () => API.get(`/notifications/activeCounts`),
    })
}

const activateDeferrerNotifications = (journalId) => {
    return API.post(`/notifications/${journalId}/activate`);

}

const useTriggers = (alwaysRefresh = false, date = null) => {
    const params = date ? `?date=${date}` : '';

    return Model({
        alwaysRefresh,
        returnDataKey: 'triggers',
        stateKey: ['notifications', 'triggers'],
        fetchData: () => API.get(`/triggers${params}`),
        registerAdditionalMethods: (opts) => {
            return {
                updateTrigger: (triggerId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/triggers/${triggerId}`, data),
                        null,
                        true,
                    );
                },
                updateTriggers: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/triggers`, data),
                        null,
                        true,
                    );
                },
                executeTrigger: (triggerId, date) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/triggers/${triggerId}/execute`, { date }),
                        null,
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading triggers.',
    })
}

const executionModes = {
    EVENT: 'Event',
    MANUAL: 'Manual',
    SCHEDULED: 'Scheduled',
    NONE: 'None',
}

const getExecutionModeLabel = (mode) => {
    switch (mode) {
        case 'Event':
            return 'Immediately';
        case 'Scheduled':
            return 'Scheduled';
        case 'Manual':
            return 'Manually';
        case 'None':
            return 'Off';
        default:
            return mode;
    }
}

const getExecutionModeHelp = (mode) => {
    switch (mode) {
        case 'Event':
            return 'Notifications are sent immediately when needed';
        case 'Scheduled':
            return 'Notifications are sent at a specific time each day';
        case 'Manual':
            return 'Notifications are only sent manually';
        case 'None':
            return 'Notifications not sent';
        default:
            return '';
    }
}

const getExecutionModeOptions = (supportedModes) => {
    if (!supportedModes) {
        return [];
    }

    return supportedModes.map(mode => ({
        value: mode,
        label: getExecutionModeLabel(mode),
        help: getExecutionModeHelp(mode),
    }));
}

const emailOptions = [
    { value: 'Send', label: 'Send' },
    { value: 'DontSend', label: "Don't send" },
];

const smsOptions = [
    { value: 'Send', label: 'Send' },
    { value: 'DontSend', label: "Don't send" },
    { value: 'SendIfNoEmail', label: 'Send if no email' },
];

const getEmailLabel = (setting) => {
    return emailOptions.find(opt => opt.value === setting)?.label ?? setting;
};

const getSMSLabel = (setting) => {
    return smsOptions.find(opt => opt.value === setting)?.label ?? setting;
};

const triggerTypes = {
    JOB_SCHEDULED : 'Job Scheduled',
    JOB_DUE_TODAY : 'Job Due Today',
    JOB_DUE_TOMORROW : 'Job Due Tomorrow',
    JOB_DUE_WEEK_UPDATED : 'Job Due Week Updated',
    JOB_MARKED_DONE_PAID : 'Job Marked Done (Paid)',
    JOB_MARKED_DONE_OWING : 'Job Marked Done (Owing)',
    JOB_SKIPPED : 'Job Skipped',
    CUSTOMER_WELCOME : 'Customer Welcome',
    CUSTOMER_MESSAGE_SENT : 'Customer Message Sent',
    CUSTOMER_REQUESTED_PORTAL_LINK : 'Customer Requested Portal Link',
    INVOICE_OVERDUE : 'Invoice Overdue',
    INVOICE_OVERDUE_2ND : 'Invoice Overdue (2nd Reminder)',
}

export const getTriggerForJobStatus = (status) => {
    switch (status) {
        case 'Scheduled':
        case 'Late':
            return triggerTypes.JOB_SCHEDULED;
        case 'Today':
            return triggerTypes.JOB_DUE_TODAY;
        case 'Tomorrow':
            return triggerTypes.JOB_DUE_TOMORROW;
        case 'Due':
        case 'Overdue':
        default:
            return null;
    }
}

export {
    useJournal,
    useJournalCounts,
    useTriggers,
    activateDeferrerNotifications,
    executionModes,
    triggerTypes,
    getExecutionModeLabel,
    getExecutionModeHelp,
    getExecutionModeOptions,
    emailOptions,
    smsOptions,
    getEmailLabel,
    getSMSLabel,
}
